/**
 * Meta Identity Slide
 *
 * Slide 2: Shows Meta (Facebook/Instagram) connection and ad count.
 */

import { motion } from 'framer-motion'
import { Facebook, Instagram, CheckCircle2 } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface MetaIdentitySlideProps {
  artifacts: OnboardingArtifacts
}

export default function MetaIdentitySlide({ artifacts }: MetaIdentitySlideProps) {
  // Extract meta identity data from artifacts
  // API returns: page_id, page_name, library_url, source
  const metaIdentity = artifacts.metaIdentity as {
    page_name?: string
    page_id?: string
    library_url?: string
    source?: string
  } | null

  // Get ads count from adsCollection artifact
  const adsCollection = artifacts.adsCollection as {
    ads_collected?: number
  } | null

  const pageName = metaIdentity?.page_name || 'Your Meta Page'
  const adsCount = adsCollection?.ads_collected || 0
  const isFound = metaIdentity?.source === 'database' || metaIdentity?.page_id

  return (
    <div className="flex flex-col items-center text-center">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-8 shadow-lg"
      >
        <Facebook className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Meta Ads Connected
      </motion.h2>

      {/* Page name */}
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-xl text-primary font-semibold mb-6"
      >
        {pageName}
      </motion.p>

      {/* Connection status */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-3 mb-8"
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span className="text-sm font-medium text-emerald-400">
            {isFound ? 'Found' : 'Identified'} on Meta Ad Library
          </span>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex items-center gap-8"
      >
        <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-tf-bg-muted border border-tf-border">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Facebook className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-left">
            <p className="text-2xl font-bold text-tf-text">{adsCount}</p>
            <p className="text-xs text-tf-text-muted">Active Ads</p>
          </div>
        </div>
        <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-tf-bg-muted border border-tf-border">
          <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
            <Instagram className="w-5 h-5 text-pink-500" />
          </div>
          <div className="text-left">
            <p className="text-2xl font-bold text-tf-text">Linked</p>
            <p className="text-xs text-tf-text-muted">Instagram</p>
          </div>
        </div>
      </motion.div>

      {/* Description */}
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-tf-text-muted max-w-md"
      >
        We found your brand on Meta&apos;s Ad Library and identified your advertising presence
      </motion.p>
    </div>
  )
}
